import dayjs, { Dayjs } from 'dayjs'
import { FC, memo, useEffect, useState, useMemo } from 'react'
import Popper, { PopperPlacementType, PopperProps } from '@mui/material/Popper'
import Fade from '@mui/material/Fade'
import { Stack, IconButton, Button, Theme, useTheme } from '@mui/material'
import {
  LocalizationProvider,
  DateCalendar,
  PickersActionBarAction,
  TimeStepOptions,
} from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { disabledGrey } from '$constants'

import CustomDateTimePickerActionBar, {
  isValueWithinRange,
} from '../CustomDateTimePickerActionBar'
import LoadedTextField from '../LoadedTextField'

export type LoadedDateTimePickerPopperProps = {
  pickerValue: Dayjs | null
  prevPickerValue: Dayjs | null
  layout: 'time' | 'date' | 'datetime'
  maxDateTime?: Dayjs | null
  minDateTime?: Dayjs | null
  timeSteps?: TimeStepOptions
  popperPlacement?: PopperPlacementType
  actions?: PickersActionBarAction[]
  required?: boolean
  handleDateChange: (date: Dayjs | null) => void
  onAccept: (date: Dayjs | null) => void
  onClear: () => void
  onCancel: () => void
  onSetToday: (currentDate: Dayjs) => void
  close: () => void
} & PopperProps

const LoadedDateTimePickerPopper: FC<LoadedDateTimePickerPopperProps> = ({
  id,
  open,
  anchorEl,
  pickerValue,
  prevPickerValue,
  layout,
  maxDateTime,
  minDateTime,
  timeSteps,
  popperPlacement = 'top',
  actions,
  required,
  handleDateChange: handleDateTimePickerChange,
  onAccept,
  onCancel,
  onClear,
  onSetToday,
  close,
}) => {
  const theme = useTheme<Theme>()
  const [value, setValue] = useState<Dayjs | null>(
    pickerValue ? dayjs(pickerValue) : null
  )

  // We store time data in an object so that when we set new values in them the
  // reference changes (we set a full new object) and this then triggers a re-render
  // of the hourString and minuteString which are used as keys in the text field
  // components along with a unique timestamp. This is to work with the fact that
  // the underlying input component allows the user to enter a value which is shown.
  // When we have decided in this code that the value they entered is invalid we
  // want to keep the original value (i.e. before they changed the input text), but
  // React thinks this is the same as it already is (as it doesn't know about the
  // inputs raw value) and optimises away the actual re-render of the input - meaning
  // the dodgy value entered would remain displayed.
  const [timeData, setTimeData] = useState<{
    hour: number | null
    minute: number | null
    isAm: boolean
  }>({
    hour: null,
    minute: null,
    isAm: true,
  })

  useEffect(() => {
    const val = pickerValue ? dayjs(pickerValue) : null
    setValue(val)
    if (val) {
      setTimeData({
        hour: Number(val.format('h')),
        minute: val.minute(),
        isAm: val.format('A') === 'AM',
      })
    } else {
      setTimeData({
        hour: null,
        minute: null,
        isAm: true,
      })
    }
  }, [pickerValue])

  const hourString = useMemo(() => {
    return timeData.hour !== null ? String(timeData.hour).padStart(2, '0') : ''
  }, [timeData.hour])
  const minuteString = useMemo(
    () =>
      timeData.minute !== null ? String(timeData.minute).padStart(2, '0') : '',
    [timeData.minute]
  )

  const updateValue = (newValue: Dayjs | null) => {
    if (
      newValue &&
      minDateTime &&
      maxDateTime &&
      !newValue.isBetween(minDateTime, maxDateTime, undefined, '[]')
    ) {
      return
    }

    setValue(newValue)
    handleDateTimePickerChange(newValue)

    if (newValue) {
      setTimeData({
        hour: Number(newValue.format('h')),
        minute: newValue.minute(),
        isAm: newValue.format('A') === 'AM',
      })
    } else {
      setTimeData({
        hour: null,
        minute: null,
        isAm: true,
      })
    }
  }

  const changeAmPm = () => {
    if (!value) return

    const newValue = timeData.isAm
      ? value.add(12, 'hour')
      : value.subtract(12, 'hour')
    updateValue(newValue)
  }

  const checkAmPmDisabled = () => {
    if (!value) return false
    if (!minDateTime && !maxDateTime) return false

    const updatedValue = timeData.isAm
      ? value.add(12, 'hour')
      : value.subtract(12, 'hour')
    if (!minDateTime && maxDateTime) return updatedValue.isAfter(maxDateTime)

    if (!maxDateTime && minDateTime) return updatedValue.isBefore(minDateTime)

    return !updatedValue.isBetween(minDateTime, maxDateTime, 'day', '[]')
  }

  const isAmPmDisabled = checkAmPmDisabled()

  if (!open) return

  const handleDateChange = (newDate: Dayjs | null) => {
    if (newDate && value) {
      const updatedValueWithTime = newDate
        .hour(value.hour())
        .minute(value.minute())
        .second(value.second())
      updateValue(updatedValueWithTime)
    } else {
      updateValue(newDate)
    }
  }

  // Helper to update the local Dayjs value with a new hour or minute value.
  // This also turns the 1-12 AM/PM hours input value into the right 00-23
  // Dayjs hours expected value.
  const handleTimeChange = (
    val: number,
    unit: 'hour' | 'minute'
  ): Dayjs | null => {
    if (value === null) return null

    let newHour = unit === 'hour' ? val : timeData.hour ?? 0
    const newMinute = unit === 'minute' ? val : timeData.minute ?? 0

    if (unit === 'hour') {
      if (timeData.isAm) {
        if (newHour === 12) newHour = 0 // 12am is 00 hours
      } else {
        if (newHour < 12) newHour += 12 // PM
      }
    }
    const newValue = value.hour(newHour).minute(newMinute)
    return newValue
  }

  const setValueOnBlur = (insertedValue: Dayjs | null) => {
    if (
      insertedValue &&
      isValueWithinRange(insertedValue, minDateTime, maxDateTime)
    ) {
      updateValue(insertedValue)
    } else {
      // Revert to last valid value on invalid blur
      setTimeData({
        hour: value ? Number(value.format('h')) : null,
        minute: value ? value.minute() : null,
        isAm: value ? value.format('A') === 'AM' : true,
      })
    }
  }

  const handleHourChange = (newHour: number) => {
    if (newHour >= 1 && newHour <= 12) {
      const newValue = handleTimeChange(newHour, 'hour')
      setValueOnBlur(newValue)
    } else {
      // hour is an invalid value - trigger a re-render with the existing value
      // to remove the dodgy user input
      setTimeData({
        ...timeData,
      })
    }
  }

  const handleMinuteChange = (newMinute: number) => {
    if (newMinute >= 0 && newMinute <= 59) {
      const newValue = handleTimeChange(newMinute, 'minute')
      setValueOnBlur(newValue)
    } else {
      // minute is an invalid value - trigger a re-render with the existing value
      // to remove the dodgy user input
      setTimeData({
        ...timeData,
      })
    }
  }

  // Helper to increment or decrement the time by the given amount
  const timeAdjuster = (adjustment: number, unit: 'hour' | 'minute') => {
    if (!value) return
    const newValue = value.add(adjustment, unit)
    updateValue(newValue)
  }

  const handleIncrementHour = () => timeAdjuster(timeSteps?.hours ?? 1, 'hour')
  const handleDecrementHour = () =>
    timeAdjuster(-(timeSteps?.hours ?? 1), 'hour')
  const handleIncrementMinute = () =>
    timeAdjuster(timeSteps?.minutes ?? 1, 'minute')
  const handleDecrementMinute = () =>
    timeAdjuster(-(timeSteps?.minutes ?? 1), 'minute')

  // Helper to check if the time adjustment is within the range that the
  // has set for the time picker
  const isTimeAdjustmentDisabled = (
    adjustment: number,
    unit: 'hour' | 'minute'
  ): boolean => {
    if (!value) return true
    const testValue = value.add(adjustment, unit)
    return !isValueWithinRange(testValue, minDateTime, maxDateTime)
  }

  const isIncrementHourDisabled = isTimeAdjustmentDisabled(
    timeSteps?.hours ?? 1,
    'hour'
  )
  const isDecrementHourDisabled = isTimeAdjustmentDisabled(
    -(timeSteps?.hours ?? 1),
    'hour'
  )
  const isIncrementMinuteDisabled = isTimeAdjustmentDisabled(
    timeSteps?.minutes ?? 1,
    'minute'
  )
  const isDecrementMinuteDisabled = isTimeAdjustmentDisabled(
    -(timeSteps?.minutes ?? 1),
    'minute'
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-nz'}>
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        transition
        placement={popperPlacement}
        sx={{
          zIndex: 2000,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.loaded.boxShadow,
          '.MuiPaper-root': {
            padding: '.5rem !important',
          },
        }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={10}>
            <Stack spacing={0}>
              <Stack direction="row" alignSelf={'center'}>
                {(layout === 'date' || layout === 'datetime') && (
                  <DateCalendar
                    value={value}
                    onChange={handleDateChange}
                    sx={{
                      height: '32.4rem',
                      width: '30rem',
                      paddingX: '1rem',
                      '& .MuiPickersDay-root.Mui-disabled': {
                        color: `${disabledGrey} !important`,
                      },
                      '.MuiDayCalendar-slideTransition': {
                        minHeight: '22.8rem !important',
                      },
                      '.MuiPickersCalendarHeader-root': {
                        marginBottom: 0,
                        marginTop: '1rem',
                        paddingLeft: '1rem',
                        paddingRight: '1rem',
                      },
                    }}
                    maxDate={maxDateTime ?? undefined}
                    minDate={minDateTime ?? undefined}
                  />
                )}

                {(layout === 'datetime' || layout === 'time') && (
                  <Stack
                    direction={'row'}
                    spacing={1}
                    paddingRight={'2rem'}
                    marginLeft={'0 !important'}
                    {...(layout === 'time' && {
                      paddingY: '1rem',
                      paddingX: '2rem',
                      justifyContent: 'center',
                      width: '100%',
                    })}
                  >
                    <Stack
                      direction="column"
                      alignItems={'center'}
                      alignSelf={'center'}
                      spacing={1}
                      marginLeft={'0 !important'}
                    >
                      <IconButton
                        size="small"
                        onClick={handleIncrementHour}
                        disabled={isIncrementHourDisabled}
                        sx={{
                          padding: 0,
                          color: theme.palette.primary.main,
                        }}
                      >
                        <KeyboardArrowUpIcon />
                      </IconButton>
                      <LoadedTextField
                        key={
                          // We want to force a re-render of the underlying input whenever
                          // we re-render. This is to make sure that we replace any manually
                          // entered user data that our own code may have decided is invalid.
                          // Without this forced uniqueness React will decide that the value
                          // of the hourString is unchanged and won't re-render the input.
                          hourString + Date.now()
                        }
                        name="hour"
                        size="small"
                        value={hourString}
                        onBlur={(e) => handleHourChange(Number(e.target.value))}
                        type="number"
                        inputProps={{
                          min: 1,
                          max: 12,
                          maxLength: 2,
                          inputMode: 'numeric',
                          sx: {
                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button':
                              {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                            MozAppearance: 'none',
                          },
                        }}
                        textFieldSxProps={{
                          textAlignLast: 'center',
                          '.MuiInputBase-root': {
                            width: '5rem',
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={handleDecrementHour}
                        disabled={isDecrementHourDisabled}
                        sx={{
                          padding: 0,
                          color: theme.palette.primary.main,
                        }}
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </Stack>
                    <Stack
                      alignItems={'center'}
                      alignSelf={'center'}
                      spacing={1}
                    >
                      <IconButton
                        size="small"
                        onClick={handleIncrementMinute}
                        disabled={isIncrementMinuteDisabled}
                        sx={{
                          padding: 0,
                          color: theme.palette.primary.main,
                        }}
                      >
                        <KeyboardArrowUpIcon />
                      </IconButton>
                      <LoadedTextField
                        key={
                          // We want to force a re-render of the underlying input whenever
                          // we re-render. This is to make sure that we replace any manually
                          // entered user data that our own code may have decided is invalid.
                          // Without this forced uniqueness React will decide that the value
                          // of the minuteString is unchanged and won't re-render the input.
                          minuteString + Date.now()
                        }
                        name="minute"
                        size="small"
                        value={minuteString}
                        onBlur={(e) =>
                          handleMinuteChange(Number(e.target.value))
                        }
                        type="number"
                        inputProps={{
                          min: 0,
                          max: 59,
                          maxLength: 2,
                          inputMode: 'numeric',
                          sx: {
                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button':
                              {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                            MozAppearance: 'none',
                          },
                        }}
                        textFieldSxProps={{
                          textAlignLast: 'center',
                          '.MuiInputBase-root': {
                            width: '5rem',
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={handleDecrementMinute}
                        disabled={isDecrementMinuteDisabled}
                        sx={{
                          padding: 0,
                          color: theme.palette.primary.main,
                        }}
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </Stack>
                    <Stack alignItems={'center'} alignSelf={'center'}>
                      <Button
                        variant="outlined"
                        disabled={isAmPmDisabled}
                        sx={{
                          height: '3.2rem',
                          padding: 0,
                          minWidth: '5rem',
                        }}
                        size="small"
                        onClick={changeAmPm}
                      >
                        {timeData.isAm ? 'AM' : 'PM'}
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Stack>
              <CustomDateTimePickerActionBar
                onAccept={() => {
                  onAccept(value)
                  close()
                }}
                onClear={() => {
                  setValue(null)
                  onClear()
                }}
                onCancel={() => {
                  setValue(prevPickerValue)
                  onCancel()
                }}
                onSetToday={() => {
                  // Original date
                  const currentDate = dayjs() // Current date and time

                  // Extract year, month, and day from the original date
                  const year = currentDate.year()
                  const month = currentDate.month() // Month is zero-based (0 for January)
                  const day = currentDate.date()

                  // New date with modified year, month, and day (time remains unchanged)
                  const newDate =
                    value
                      ?.set('date', day)
                      .set('month', month)
                      .set('year', year) || dayjs().startOf('day')

                  updateValue(newDate)
                  onSetToday(newDate)
                }}
                actions={actions}
                layout={layout}
                required={required}
                maxDateTime={maxDateTime}
                minDateTime={minDateTime}
                value={value}
              />
            </Stack>
          </Fade>
        )}
      </Popper>
    </LocalizationProvider>
  )
}

export default memo(LoadedDateTimePickerPopper)
